import { Box, Button, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { PlanCard } from './components';
import { COLORS } from './tokens';
import type { PlanReviewer, PlanSignOff, SignOffRule } from './planRecordApi';
import { displayYear, formatPlanDate } from './planRecordView';

const ROW_TEXT = { fontSize: 16, fontWeight: 500, lineHeight: '24px' } as const;

function statusText(r: PlanReviewer): string {
  if (r.status === 'reviewed') return `Reviewed ${formatPlanDate(r.signedAt)}`;
  if (r.status === 'earlier') return `Not yet · reviewed the ${formatPlanDate(r.signedAt)} version`;
  return 'Not yet';
}

function Row({ name, role, status, done }: { name: string; role: string; status: string; done: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 40, borderBottom: `1px solid ${COLORS.divider}`, '&:last-of-type': { borderBottom: 0 } }}>
      {done ? (
        <CheckRoundedIcon sx={{ fontSize: 20, color: COLORS.navy }} />
      ) : (
        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '8px', border: `1px solid ${COLORS.faint}` }} />
        </Box>
      )}
      <Typography sx={{ ...ROW_TEXT, width: 200, flexShrink: 0, color: COLORS.ink }}>{name}</Typography>
      <Typography sx={{ ...ROW_TEXT, width: 200, flexShrink: 0, color: COLORS.muted }}>{role}</Typography>
      <Typography sx={{ ...ROW_TEXT, flex: 1, color: COLORS.muted }}>{status}</Typography>
    </Box>
  );
}

export function SignOffsCard({
  firstName,
  districtName,
  year,
  rule,
  rosteringKnown,
  reviewers,
  alsoReviewed,
  onAddReviewer,
}: {
  firstName: string;
  districtName: string;
  year: string;
  rule: SignOffRule;
  rosteringKnown: boolean;
  reviewers: PlanReviewer[];
  alsoReviewed: PlanSignOff[];
  onAddReviewer: () => void;
}) {
  const shownYear = displayYear(year);
  const done = reviewers.filter((r) => r.status === 'reviewed').length;
  const nobody = !rosteringKnown || reviewers.length === 0;
  let subtitle: string;
  if (nobody) {
    subtitle = `No teachers of record yet. ${districtName} hasn't finished RosterStream setup, so nobody has been asked. Add reviewers by hand for now; once rostering is set up, ${firstName}'s teachers of record are asked automatically.`;
  } else if (rule === 'el_teacher_only') {
    const first = reviewers[0]!;
    subtitle = `Your district asks the EL teacher to sign off. ${first.status === 'reviewed' ? `${first.name} reviewed the ${shownYear} plan on ${formatPlanDate(first.signedAt)}.` : `${first.name} hasn't reviewed the ${shownYear} plan yet.`} Other teachers can be added.`;
  } else {
    subtitle = `${done} of ${reviewers.length} have reviewed the ${shownYear} plan. ${firstName}'s teachers of record come from your rostering.`;
  }
  const addReviewer = (
    <Button onClick={onAddReviewer} variant={nobody ? 'contained' : 'text'} sx={{ textTransform: 'none', fontWeight: 600, fontSize: nobody ? 16 : 12, flexShrink: 0, ...(nobody ? { bgcolor: COLORS.navy, '&:hover': { bgcolor: '#23236F' } } : { color: COLORS.navy, p: 0, minWidth: 0 }) }}>
      Add a reviewer
    </Button>
  );
  return (
    <PlanCard title="Sign-offs" subtitle={subtitle} right={nobody ? addReviewer : undefined} sx={{ gap: 2 }}>
      {!nobody && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {reviewers.map((r) => (
            <Row key={`${r.name}|${r.role}`} name={r.name} role={r.role} status={statusText(r)} done={r.status === 'reviewed'} />
          ))}
        </Box>
      )}
      {alsoReviewed.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: '16px', color: COLORS.muted }}>Also reviewed</Typography>
          {alsoReviewed.map((s) => (
            <Row key={`${s.name}|${s.version}`} name={s.name} role="" status={`Reviewed ${formatPlanDate(s.signedAt)}`} done />
          ))}
        </Box>
      )}
      {!nobody && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: '16px', color: COLORS.muted }}>Missing someone? Rostering decides who is asked.</Typography>
          {addReviewer}
        </Box>
      )}
    </PlanCard>
  );
}
